import React from 'react';
import PropTypes from 'prop-types';
// `components/Paginator` is AMBIGUOUS in this repo: it resolves to one of
// two API-incompatible files - src/view/components/Paginator.js (prop
// `setPage`, used below) or src/view/components/Paginator/Paginator.jsx
// (prop `onPageChange`). Node's file-before-directory resolution picks the
// .js file, so `setPage` below is correct BY LUCK, not by an unambiguous
// import path. If a future reorganisation removes Paginator.js (leaving
// only the Paginator/ directory), this import would silently start
// resolving to the OTHER component and `setPage` would stop firing with
// no error - grep for both paths before touching either.
import Paginator from 'components/Paginator';
import { EM_DASH, formatNumber, formatEtDateTime } from './datasetMonitorFormat';

const RESULT_QUEUED = 'Queued';
const RESULT_SENT = 'Sent';
const RESULT_FAILED = 'Failed';

const RESULT_BADGE_CLASS = {
	[RESULT_QUEUED]: 'bg-secondary',
	[RESULT_SENT]: 'bg-success',
	[RESULT_FAILED]: 'bg-danger',
};

/**
 * SendsTable
 *
 * Paginated queued_emails for one dataset (design spec 10 `sends`):
 * Sent | Type | Recipient | Property | Records | Result.
 *
 * Result is deliberately only Queued | Sent | Failed - never
 * Delivered/Opened/Bounced. Nothing in this codebase writes an SES/SNS
 * delivery status today (no webhook, `STATUS_BOUNCED` is only ever read),
 * so printing "Delivered" next to a bounced email would be exactly the
 * failure this page exists to catch (design spec 11).
 */
const SendsTable = ({ sends, isLoading, currentPage, totalPages, onPageChange }) => {
	if (!isLoading && sends.length === 0) {
		return <p className="text-muted">No sends yet for this dataset.</p>;
	}

	return (
		<div className="sends-table">
			<div className="table-responsive">
				<table className="table table-sm table-hover">
					<thead className="table-light">
						<tr>
							<th>Sent</th>
							<th>Type</th>
							<th>Recipient</th>
							<th>Property</th>
							<th className="text-end">Records</th>
							<th>Result</th>
						</tr>
					</thead>
					<tbody>
						{sends.map((send) => (
							<tr key={send.id}>
								<td>
									{send.sent_at ? `${formatEtDateTime(send.sent_at)} ET` : EM_DASH}
								</td>
								<td>{send.type || EM_DASH}</td>
								<td>{send.recipient || EM_DASH}</td>
								{/* Field is `property` on the wire
								    (DatasetMonitorController::mapSend() ->
								    $email->property?->address), not
								    `property_address` - reconciled against the
								    backend's actual mapSend() shape, which is
								    the authority (review finding). Null when a
								    queued email has no property association. */}
								<td>{send.property || EM_DASH}</td>
								<td className="text-end">{formatNumber(send.records)}</td>
								<td>
									<span
										className={`badge ${RESULT_BADGE_CLASS[send.result] || 'bg-secondary'}`}
									>
										{send.result || EM_DASH}
									</span>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{totalPages > 1 && (
				<Paginator
					currentPage={currentPage}
					totalPages={totalPages}
					setPage={onPageChange}
				/>
			)}
		</div>
	);
};

SendsTable.propTypes = {
	sends: PropTypes.array,
	isLoading: PropTypes.bool,
	currentPage: PropTypes.number,
	totalPages: PropTypes.number,
	onPageChange: PropTypes.func,
};

SendsTable.defaultProps = {
	sends: [],
	isLoading: false,
	currentPage: 1,
	totalPages: 1,
	onPageChange: () => {},
};

export default SendsTable;
